import { memo, useMemo } from 'react';
import Cyclorama from '../Presentation/Studio/Cyclorama';
import Pedestal from '../Presentation/Studio/Pedestal';
import {
    DEFAULT_MODEL_VIEWER_PEDESTAL,
    DEFAULT_MODEL_VIEWER_PEDESTAL_MATERIAL,
    getModelViewerPedestalLayout,
    type ModelViewerPedestal,
    type ModelViewerPedestalMaterial,
} from '../Presentation/Pedestal';
import CachedShadowPlane from './CachedShadowPlane';
import ShadowBaker from './ShadowBaker';
import type { ShadowModel } from './types';
import useBakedShadow from './useBakedShadow';
import useShadowReadiness from './useShadowReadiness';

interface Props {
    cycloramaColor?: string
    cycloramaGlossiness?: number
    fadeEdges?: boolean
    lightRotationY?: number
    model?: ShadowModel
    pedestal?: ModelViewerPedestal
    pedestalMaterial?: ModelViewerPedestalMaterial
    progressive?: boolean
    reflectionRevision?: number
    transmissionRevision?: number
}

/**
 * Composes the hidden accumulative baker with one of two cached receivers:
 * a flat plane for the Neutral setup or a projected cyclorama overlay for
 * Studio. Baking, GPU readback, caching, and renderbot readiness live in the
 * focused modules beside this component.
 */
export default memo(function ContactShadows({
    cycloramaColor,
    cycloramaGlossiness = 0,
    fadeEdges = false,
    lightRotationY,
    model,
    pedestal = DEFAULT_MODEL_VIEWER_PEDESTAL,
    pedestalMaterial = DEFAULT_MODEL_VIEWER_PEDESTAL_MATERIAL,
    progressive = false,
    reflectionRevision,
    transmissionRevision,
}:Props) {
    const {
        scratchVisible,
        shadowsRef,
        visibleShadow,
    } = useBakedShadow(model, progressive);
    const pedestalLayout = useMemo(() => (
        model ? getModelViewerPedestalLayout(model, pedestal) : undefined
    ), [model, pedestal]);

    useShadowReadiness(model, visibleShadow);

    return <>
        {model && cycloramaColor !== undefined
            ? <Cyclorama
                color={cycloramaColor}
                depth={model.depth}
                floorY={pedestalLayout?.floorY}
                floorOverlayRadius={pedestalLayout?.radius}
                glossiness={cycloramaGlossiness}
                height={model.height}
                radius={model.radius}
                reflectionRevision={reflectionRevision}
                shadowTexture={pedestalLayout
                    ? undefined
                    : visibleShadow?.entry.texture}
            />
            : null}
        {pedestalLayout && cycloramaColor !== undefined
            ? <Pedestal
                cycloramaColor={cycloramaColor}
                layout={pedestalLayout}
                material={pedestalMaterial}
                transmissionRevision={transmissionRevision}
            />
            : null}
        <ShadowBaker
            ref={shadowsRef}
            lightRotationY={lightRotationY}
            model={model}
            progressive={progressive}
            visible={scratchVisible}
        />
        {model && visibleShadow && pedestalLayout
            ? <CachedShadowPlane
                fadeEdges={false}
                model={model}
                pedestal={pedestalLayout}
                texture={visibleShadow.entry.texture}
            />
            : null}
        {model && visibleShadow && cycloramaColor === undefined
            ? <CachedShadowPlane
                fadeEdges={fadeEdges}
                model={model}
                texture={visibleShadow.entry.texture}
            />
            : null}
    </>;
});
